/* Add Applicant + pitch-deck upload. The VC uploads an applicant at the point their
   funding form is submitted: fill the funding-form fields and attach the deck, then
   POST /api/deal/ creates the deal, runs the zero-LLM gate and files the deck to S3.
   On a pass the deal opens at intake (ready for AI analysis); on a gate fail it opens
   at intake showing the dealbreaker. Dropdown options come from the investor profile
   lookups (GET /deal/profile); when absent they fall back to free-text inputs. */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useProfile } from '../state';
import { useToast } from './Toast';
import ModalPortal from './ModalPortal';
import { BASE } from '../helpers';
import { addApplicant, ok } from '../api';

// Render a <select> when the profile supplies options, else a plain text input.
function LookupField({ label, value, onChange, options, placeholder }) {
	const opts = Array.isArray(options) ? options : [];
	return (
		<label className="dm-field">
			<span className="dm-field__label">{label}</span>
			{opts.length ? (
				<select value={value} onChange={e => onChange(e.target.value)}>
					<option value="">Select…</option>
					{opts.map(o => <option key={o} value={o}>{o}</option>)}
				</select>
			) : (
				<input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
			)}
		</label>
	);
}

export default function AddApplicantModal({ onClose, onAdded }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const { profile } = useProfile();
	const lookups = (profile && profile.lookups) || {};

	const [name, setName] = useState('');
	const [founderContact, setFounderContact] = useState('');
	const [sector, setSector] = useState('');
	const [stage, setStage] = useState('');
	const [ask, setAsk] = useState('');
	const [region, setRegion] = useState('');
	const [revenue, setRevenue] = useState('');
	const [file, setFile] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const canSubmit = name.trim() && file && !submitting;

	const submit = async () => {
		if (!canSubmit) return;
		setSubmitting(true);
		const payload = { name: name.trim(), founderContact, sector, stage, ask, region, revenue, attachment: [file] };
		const res = await addApplicant(payload);
		setSubmitting(false);
		if (!ok(res)) return;                         // request helper toasts the backend message
		const body = res.body || {};
		const passed = body.gateOutcome !== 'fail';
		showToast(passed ? `${name.trim()} added — passed the gate` : `${name.trim()} added — ruled out: ${body.dealbreaker || 'dealbreaker'}`);
		onClose();
		if (onAdded) onAdded();
		if (body.deal && body.deal.id) navigate(`${BASE}/deal/${body.deal.id}/intake`);
	};

	return (
		<ModalPortal>
		<div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
			<div className="modal modal--form" role="dialog" aria-label="Add applicant">
				<div className="modal__head">
					<div className="modal__title">Add applicant</div>
					<div className="modal__desc">Upload the applicant's pitch deck and funding-form details. The deck is filed to your data room and the auto-decline gate runs instantly — no AI spend until you run analysis.</div>
				</div>

				<div className="modal__body">
				<div className="dm-formgrid">
					<label className="dm-field dm-field--full">
						<span className="dm-field__label">Company name *</span>
						<input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kestrel Logistics" />
					</label>
					<label className="dm-field dm-field--full">
						<span className="dm-field__label">Founder contact email</span>
						<input type="email" value={founderContact} onChange={e => setFounderContact(e.target.value)} placeholder="founder@company.com" />
					</label>
					<LookupField label="Sector" value={sector} onChange={setSector} options={lookups.sectors} placeholder="e.g. B2B SaaS" />
					<LookupField label="Stage" value={stage} onChange={setStage} options={lookups.stages} placeholder="e.g. Seed" />
					<label className="dm-field">
						<span className="dm-field__label">Ask</span>
						<input type="text" value={ask} onChange={e => setAsk(e.target.value)} placeholder="e.g. £1.5M" />
					</label>
					<LookupField label="Region" value={region} onChange={setRegion} options={lookups.regions} placeholder="e.g. UK" />
					<label className="dm-field dm-field--full">
						<span className="dm-field__label">Revenue (last 12m)</span>
						<input type="text" value={revenue} onChange={e => setRevenue(e.target.value)} placeholder="e.g. £338k ARR or Pre-revenue" />
					</label>
					<label className="dm-field dm-field--full">
						<span className="dm-field__label">Pitch deck *</span>
						<input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,.key" onChange={e => setFile(e.target.files && e.target.files[0])} />
						{file ? <span className="dm-field__hint">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span> : <span className="dm-field__hint">PDF, PowerPoint or Word — up to 100 MB</span>}
					</label>
				</div>
				</div>

				<div className="modal__actions">
					<button className="pill pill--ghost" onClick={onClose} disabled={submitting}>Cancel</button>
					<button className="pill pill--primary" onClick={submit} disabled={!canSubmit}>{submitting ? 'Uploading…' : 'Add & run gate'}</button>
				</div>
			</div>
		</div>
		</ModalPortal>
	);
}
